import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, X, RefreshCw, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { resendVerificationOtp } from '../api/account';
import { useToast } from '../hooks/use-toast';

/**
 * Global banner shown to logged-in users with email_verified === false.
 * Sticks under the topbar and links to /settings where the OTP code can
 * be entered. Dismissible per-session (sessionStorage flag).
 */
const EmailVerifyBanner = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation('common');
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem('nymia.email_banner_dismissed') === '1',
  );
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  if (!user || user.email_verified || dismissed) return null;

  const target = user.pending_email || user.email;

  const close = () => {
    sessionStorage.setItem('nymia.email_banner_dismissed', '1');
    setDismissed(true);
  };

  const startCooldown = (seconds) => {
    setCooldown(seconds);
    const tick = () => {
      setCooldown((c) => {
        if (c <= 1) return 0;
        setTimeout(tick, 1000);
        return c - 1;
      });
    };
    setTimeout(tick, 1000);
  };

  const resend = async () => {
    if (cooldown > 0) return;
    setSending(true);
    try {
      await resendVerificationOtp();
      toast({
        title: t('email_verify_banner.toast_sent_title'),
        description: t('email_verify_banner.toast_sent_desc', { email: target }),
      });
      startCooldown(60);
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (e.response?.status === 429 && detail.startsWith('rate_limited|')) {
        toast({
          title: t('email_verify_banner.toast_wait_title'),
          description: detail.split('|').pop(),
          variant: 'destructive',
        });
        startCooldown(60);
      } else {
        toast({
          title: t('email_verify_banner.toast_error_title'),
          description: detail || t('email_verify_banner.toast_error_default'),
          variant: 'destructive',
        });
      }
    } finally { setSending(false); }
  };

  return (
    <div
      className="sticky top-0 z-40 flex flex-wrap items-center justify-center gap-3 border-b border-amber-500/40 bg-amber-500/20 px-4 py-2 text-xs text-amber-100"
      data-testid="email-verify-banner"
    >
      <ShieldAlert className="h-4 w-4 shrink-0 gold-text" />
      <p className="leading-snug text-foreground">
        {t('email_verify_banner.prefix')} <span className="font-semibold gold-text">{target}</span> {t('email_verify_banner.suffix')}{' '}
        <Link
          to="/settings"
          className="font-semibold underline underline-offset-2 gold-text hover:text-amber-200"
          data-testid="email-verify-banner-link"
        >
          {t('email_verify_banner.action')}
        </Link>
      </p>
      <button
        onClick={resend}
        disabled={sending || cooldown > 0}
        className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[11px] font-semibold gold-text hover:bg-amber-500/30 disabled:opacity-50"
        data-testid="email-verify-resend-btn"
      >
        {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
        {cooldown > 0
          ? t('email_verify_banner.resend_countdown', { seconds: cooldown })
          : t('email_verify_banner.resend')}
      </button>
      <button
        onClick={close}
        className="ml-2 rounded-md p-1 text-amber-100/70 hover:bg-amber-500/20 hover:text-amber-100"
        aria-label={t('email_verify_banner.dismiss_aria')}
        data-testid="email-verify-banner-dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export default EmailVerifyBanner;
